/*
 * When the page load we call rampjs-server to get a config.
  Once we get the config we call google's csa script to inject into the page.
  The csa script will call our adLoadedCallback function when the ads are loaded.
  We will send a pingback back to papi to let them know that the ad was loaded
  -if after 10 seconds we do not get a callback from google, we will send a pingback to papi to let them know
  -if the user leaves the page before the callback was loaded we will send a pingback to papi to let them know
  -when the dom is ready we will check to make sure the divs are there if they are not, we will send a pingback to papi to let them know
*/

import { CONSOLE_CAPTURE_PREFIX, DEBUG_ASSERTIONS } from "@ascent/debugger";
import type * as PAPICore from "@ascent/papi-core";
import { appendPartnerCookies, sendPingback } from "@ascent/papi-core/client";
import type { Response } from "@rampjs/server";
import type { AdLoadedCallback } from "../types";
import { rampJsAdsLoaded, rampJsRsocFailed, rampJsUnitLoaded } from "./events";
import {
  CSA_SCRIPT_SRC as src,
  CSA_STUB as text,
} from "./modules/google-csa/script";
import { getUnitGateDiagnostics } from "./rsoc-gate";
import {
  TrackingEvent,
  observeAdContainer,
  observeInView,
} from "./tracking-events";
import { inlineScript, loadScript, onDomReady } from "./util";

// pendingUnits is managed in initAdCallbackTracking, but we need to access it here.
// We'll attach it to window for now for cross-function access (or refactor to a module-level variable if preferred).
// Add this at the top-level (outside any function):
const globalPendingUnits: Map<string, string> =
  (window as any)._rampjsPendingUnits || new Map();
(window as any)._rampjsPendingUnits = globalPendingUnits;

/**
 * Creates an ad loaded callback function for Google RSoC.
 *
 * @param callback - The callback configuration containing parameters for the URL.
 * @param unit_level_params - The parameters specific to the ad unit level.
 * @returns A function that sends a beacon to the ad loaded callback URL when ads are loaded.
 */
export function buildPingbackUrl(
  baseUrl: string,
  callback: PAPICore.Callback,
  unit_level_params: PAPICore.UnitLevelParams,
  adsLoaded?: boolean,
  callbackOptions?: unknown,
  extraParams?: Record<string, string>,
): URL {
  const url = new URL(baseUrl);
  if (callback && adsLoaded !== undefined && callback.loaded_param) {
    url.searchParams.set("loaded_param", callback.loaded_param);
    url.searchParams.set(callback.loaded_param, String(adsLoaded));
  }
  if (callback && callbackOptions && callback.options_param) {
    url.searchParams.set("options_param", callback.options_param);
    url.searchParams.set(
      callback.options_param,
      JSON.stringify(callbackOptions),
    );
  }
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      url.searchParams.set(key, value);
    }
  }
  // ABE-850: tag pages carrying injectable units with whether the unit gate
  // held them back, so RSoC RPM can be compared gated vs ungated.
  for (const [key, value] of Object.entries(getUnitGateDiagnostics())) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }

  appendPartnerCookies(url);
  appendFbDiagnostics(url);

  return url;
}

/**
 * Append Facebook-pixel diagnostics to a pingback URL so we can correlate
 * missing `_fbc` cookies with landing-page state and script-load state.
 *
 * Fields:
 *  - fb_expected: server response included partner_tracking.fbid (0/1)
 *  - fb_init_called: initFacebook() was invoked client-side (0/1)
 *  - fb_has_fbclid: current URL's query string contains fbclid (0/1)
 *  - fb_script_ready: window.fbq is present and loaded (0/1)
 *  - fb_ms_since_init: ms between initFacebook() call and this pingback
 *  - fb_referrer_host: bucket of document.referrer hostname
 *  - fb_referrer_url: full document.referrer (debug-only; remove once investigation concludes)
 *
 * Cookie presence is already conveyed by the existing `_fbc` / `_fbp`
 * query params (set only when the cookie exists), so no extra flags for
 * those are needed.
 */
function appendFbDiagnostics(url: URL): void {
  const fbState = (window as Window)._rampjsFbState;

  if (!url.searchParams.has("fb_expected")) {
    url.searchParams.set("fb_expected", fbState?.fbidPresent ? "1" : "0");
  }
  if (!url.searchParams.has("fb_init_called")) {
    url.searchParams.set("fb_init_called", fbState?.initCalled ? "1" : "0");
  }

  if (!url.searchParams.has("fb_has_fbclid")) {
    let hasFbclid = 0;
    try {
      hasFbclid = new URLSearchParams(window.location.search).has("fbclid")
        ? 1
        : 0;
    } catch {
      hasFbclid = 0;
    }
    url.searchParams.set("fb_has_fbclid", String(hasFbclid));
  }

  if (!url.searchParams.has("fb_script_ready")) {
    const fbq = (window as unknown as { fbq?: { loaded?: boolean } }).fbq;
    url.searchParams.set(
      "fb_script_ready",
      fbq && fbq.loaded === true ? "1" : "0",
    );
  }

  if (!url.searchParams.has("fb_ms_since_init") && fbState?.initCalledAt) {
    url.searchParams.set(
      "fb_ms_since_init",
      String(Math.max(0, Date.now() - fbState.initCalledAt)),
    );
  }

  if (!url.searchParams.has("fb_referrer_host")) {
    url.searchParams.set("fb_referrer_host", bucketReferrerHost());
  }

  if (!url.searchParams.has("fb_referrer_url") && document.referrer) {
    url.searchParams.set("fb_referrer_url", document.referrer);
  }
}

function bucketReferrerHost(): string {
  const ref = document.referrer;
  if (!ref) return "(none)";
  let host = "";
  try {
    host = new URL(ref).hostname.toLowerCase();
  } catch {
    return "(other)";
  }
  if (host === "facebook.com" || host.endsWith(".facebook.com")) {
    return host === "l.facebook.com" || host === "lm.facebook.com"
      ? host
      : "facebook.com";
  }
  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    return "instagram.com";
  }
  return "(other)";
}

export function adLoadedCallback(
  callback: PAPICore.Callback,
  unit_level_params: PAPICore.UnitLevelParams,
  emitAdLoadedEvent: boolean = false,
): AdLoadedCallback {
  //lets check for the missing div here because if the div is missing, google will never
  //call the callback
  //wait for the dom to be ready before checking
  const containerName = unit_level_params.container;
  if (typeof containerName === "string" && containerName.length > 0) {
    onDomReady(() => {
      if (unit_level_params.adLoadedCallback) {
        const container = document.getElementById(containerName);
        if (!container) {
          // Send pingback for missing div
          const url = buildPingbackUrl(
            unit_level_params.adLoadedCallback,
            callback,
            unit_level_params,
            undefined,
            undefined,
            {
              rsonc_no_callback: "missing_divs",
              container: containerName,
            },
          );
          sendPingback(url);
          if (__DEBUG__) {
            console.log(
              `${CONSOLE_CAPTURE_PREFIX} Pingback sent for missing div: '${containerName}'`,
              url,
            );
          }
          // Remove from pendingUnits if present
          globalPendingUnits.delete(containerName);
        }
      }
    });
  }

  return (containerName, adsLoaded, isExperimentVariant, callbackOptions) => {
    if (__DEBUG__) {
      console.log(
        `${CONSOLE_CAPTURE_PREFIX} adLoadedCallback`,
        containerName,
        adsLoaded,
        isExperimentVariant,
        callbackOptions,
      );
    }

    if (unit_level_params.adLoadedCallback) {
      const url = buildPingbackUrl(
        unit_level_params.adLoadedCallback,
        callback,
        unit_level_params,
        adsLoaded,
        callbackOptions,
      );
      sendPingback(url);
    }
    if (emitAdLoadedEvent) {
      window.dispatchEvent(
        rampJsAdsLoaded(
          containerName,
          adsLoaded,
          isExperimentVariant,
          callbackOptions,
        ),
      );
    }
    window.dispatchEvent(rampJsUnitLoaded(containerName, "csa"));
  };
}

/**
 * Checks if a given group is a Google RSoC group.
 *
 * @param group - The group to check.
 * @returns `true` if the group is a Google RSoC group, otherwise `false`.
 */
export function isGroup(group: PAPICore.RegionGroup): boolean {
  return group.backend === "google" && group.result_type === "rsoc";
}

/**
 * Handles the server response for RAMP.js and initializes Google RSoC scripts.
 *
 * @param server - The server response containing search results and options.
 *
 * @returns void
 */
export function onResponse(server: Response) {
  // Inline <script> once.
  if (typeof _googCsa === "undefined") {
    window.__rampjsCsaHostPage = true;
    inlineScript(text);
  }

  // Load <script> once.
  if (document.querySelector(`script[src="${src}"]`) === null) {
    loadScript(src).catch(() => {
      // No adLoadedCallback can arrive now — release the unit gate early.
      window.dispatchEvent(rampJsRsocFailed("csa_script_failed_to_load"));
      // Send a pingback for each RSoC unit if the script fails to load
      if (server?.search?.response?.page_options?.rsoc) {
        const { callback } = server.search.response.page_options.rsoc;
        if (callback) {
          for (const region of server.search.response.regions) {
            const groups = region.groups.filter(isGroup);
            for (const group of groups) {
              const unit_level_params =
                group.unit_level_params as PAPICore.UnitLevelParams;
              if (unit_level_params && unit_level_params.adLoadedCallback) {
                const url = buildPingbackUrl(
                  unit_level_params.adLoadedCallback,
                  callback,
                  unit_level_params,
                  undefined,
                  undefined,
                  {
                    csa_script_failed: "1",
                    rsonc_no_callback: "csa_script_failed_to_load",
                  },
                );
                sendPingback(url);
              }
            }
          }
        }
      }
      if (__DEBUG__) {
        console.error("[RampJs] Failed to load Google RSoC script.");
      }
    });
  }

  if (__DEBUG__) {
    DEBUG_ASSERTIONS.push([
      !!server,
      "RampJs Server response is present",
      undefined,
      "RampJs",
    ]);
    DEBUG_ASSERTIONS.push([
      !!server.search,
      "Server response contains Papi-Core Response",
      undefined,
      "Papi-Core",
    ]);
    DEBUG_ASSERTIONS.push([
      Array.isArray(server.search?.response?.regions),
      "Regions are missing from Papi-Core Response",
      undefined,
      "Papi-Core",
    ]);
  }

  if (!server?.search?.response) {
    if (__DEBUG__) {
      console.warn("[RampJs] No Papi-Core Response in server response.");
    }
    return;
  }

  const { page_options, regions } = server.search.response;
  if (!page_options?.rsoc) {
    if (__DEBUG__) {
      console.warn("[RampJs] No RSoC options in server response.");
    }
    return;
  }
  const { callback, csa_page_options } = page_options.rsoc;
  const rampjsOpts = server.search.response.render_options?.rampjs as
    | { emit_ad_loaded_event?: boolean }
    | undefined;
  const emitAdLoadedEvent = rampjsOpts?.emit_ad_loaded_event === true;
  const units: Record<string, unknown>[] = [];

  for (const region of regions) {
    const groups = region.groups.filter(isGroup);
    for (const group of groups) {
      const unit_level_params =
        group.unit_level_params as PAPICore.UnitLevelParams;
      if (!unit_level_params) continue;
      //const containerName = unit_level_params.container as string;
      const unit = {
        ...unit_level_params,
        adLoadedCallback:
          unit_level_params.__wrappedAdLoadedCallback ||
          adLoadedCallback(callback!, unit_level_params!, emitAdLoadedEvent),
      };

      const beaconKeys = [
        "clickBeaconUrl",
        "impressionBeaconUrl",
        "clicktrackUrl",
      ];
      for (const key of beaconKeys) {
        const val = (unit_level_params as Record<string, unknown>)[key];
        if (typeof val === "string") {
          try {
            const url = new URL(val);
            appendPartnerCookies(url);
            (unit as Record<string, unknown>)[key] = url.toString();
          } catch {
            // keep original URL if parsing fails
          }
        }
      }

      units.push(unit);
    }
  }
  if (units.length > 0) {
    if (csa_page_options) {
      if (__DEBUG__) {
        console.log(`${CONSOLE_CAPTURE_PREFIX} Page Options`, csa_page_options);
        console.log(`${CONSOLE_CAPTURE_PREFIX} Units`, ...units);
      }

      const callCsa = () =>
        _googCsa("relatedsearch", csa_page_options, ...units);

      if (
        __DEBUG__ &&
        new URLSearchParams(window.location.search).has("rampjs_delay_csa")
      ) {
        console.log(
          `${CONSOLE_CAPTURE_PREFIX} _googCsa call is PAUSED (rampjs_delay_csa detected).`,
          `\n  Call _rampjsResumeCsa() to resume.`,
          `\n  Use _rampjsDebugPingback(reason) to test pingback scenarios while paused.`,
        );
        (window as any)._rampjsResumeCsa = () => {
          console.log(`${CONSOLE_CAPTURE_PREFIX} Resuming _googCsa call...`);
          callCsa();
          delete (window as any)._rampjsResumeCsa;
        };
      } else {
        callCsa();
      }
    }
  }

  if (__DEBUG__) {
    DEBUG_ASSERTIONS.push([
      units.length > 0,
      "Units were returned from server response",
    ]);
    // should this always be there? What if we are not loading csa units, this might create confusion, for now
    //we only support csa units so, whatever
    DEBUG_ASSERTIONS.push([
      csa_page_options !== undefined,
      "csa_page_options were returned from server response",
    ]);
  }
}

/**
 * Initializes ad callback tracking and pingback logic for Google RSoC units.
 *
 * @param server - The server response containing search results and options.
 * @param timeoutMs - Timeout in milliseconds for ad callback pingbacks.
 */
export function initAdCallbackTracking(
  server: Response,
  timeoutMs: number = 10000,
) {
  // Track ad units expecting callbacks
  const pendingUnits = globalPendingUnits;

  // Helper to send the never-ran pingback with a reason
  function sendNeverRanPingback(
    adLoadedCallbackUrl: string,
    callback: PAPICore.Callback,
    unit_level_params: PAPICore.UnitLevelParams,
    reason: string,
  ) {
    const url = buildPingbackUrl(
      adLoadedCallbackUrl,
      callback,
      unit_level_params,
      undefined,
      undefined,
      {
        rsonc_no_callback: reason,
        container: unit_level_params.container ?? "",
      },
    );
    sendPingback(url);
  }

  // Mark callback as received
  function markCallbackReceived(containerName: string) {
    pendingUnits.delete(containerName);
  }

  // Patch adLoadedCallback to track calls
  function wrapAdLoadedCallback(
    orig: (...args: any[]) => void,
    adLoadedCallbackUrl: string,
  ): (...args: any[]) => void {
    return function (this: unknown, containerName: string, ...args: any[]) {
      markCallbackReceived(containerName);
      return orig.apply(this, [containerName, ...args]);
    };
  }

  // if there is no server response, we can't do anything
  if (!server?.search?.response) {
    console.warn("[RampJs] No Papi-Core Response in server response.");
    return;
  }

  // Patch adLoadedCallback for each unit to track calls
  const { page_options, regions } = server.search.response;
  const rampjsOpts = server.search.response.render_options?.rampjs as
    | { emit_ad_loaded_event?: boolean }
    | undefined;
  const emitAdLoadedEvent = rampjsOpts?.emit_ad_loaded_event === true;
  if (page_options?.rsoc) {
    const { callback } = page_options.rsoc;
    if (callback) {
      for (const region of regions) {
        const groups = region.groups.filter(isGroup);
        for (const group of groups) {
          const unit_level_params =
            group.unit_level_params as PAPICore.UnitLevelParams;
          if (
            unit_level_params &&
            typeof unit_level_params.adLoadedCallback === "string"
          ) {
            const containerName = unit_level_params.container as string;
            if (containerName) {
              pendingUnits.set(
                containerName,
                unit_level_params.adLoadedCallback as string,
              );
            }
            // Instead of overwriting adLoadedCallback, store the wrapped callback for use in the ad system
            unit_level_params.__wrappedAdLoadedCallback = wrapAdLoadedCallback(
              adLoadedCallback(callback, unit_level_params, emitAdLoadedEvent),
              unit_level_params.adLoadedCallback as string,
            );
          }
        }
      }
    }
  }

  // Track the ad-load container observers (mutation_observed / resize_observed)
  // for cleanup on timeout / page exit. in_view is NOT tracked here — it is a
  // viewport signal with a page lifetime (see below), so tearing it down on the
  // ad-load timeout would drop it for any unit scrolled into view after the
  // timeout window. Wired via the shared helpers (ABE-864), shared with CSA.
  const containerObservers: Array<{ disconnect: () => void }> = [];
  // The ad-load observers are wired inside a deferred onDomReady callback, while
  // the timeout / page-exit handlers below can run first (e.g. DOM parse slower
  // than the timeout). Guard against wiring — and leaking — ad-load observers
  // after cleanup has already happened. in_view is exempt: it is a page-lifetime
  // signal and must still be wired even if the ad-load window has closed.
  let disposed = false;

  // Set up the standardized container observers for each RSoC container. The
  // per-event URL construction (buildPingbackUrl with the event as a
  // `<event>=true` param) is RSoC's wire format; the observation logic lives in
  // the shared observeAdContainer / observeInView helpers so the CSA path emits
  // the same signals.
  if (page_options?.rsoc?.callback) {
    const { callback } = page_options.rsoc;
    if (callback) {
      for (const region of regions) {
        const groups = region.groups.filter(isGroup);
        for (const group of groups) {
          const unit_level_params =
            group.unit_level_params as PAPICore.UnitLevelParams;
          if (!unit_level_params || !unit_level_params.adLoadedCallback)
            continue;
          const containerName = unit_level_params.container;
          if (typeof containerName !== "string" || !containerName) continue;

          const adLoadedCallbackUrl =
            unit_level_params.adLoadedCallback as string;

          const fireEvent = (event: TrackingEvent) => {
            const url = buildPingbackUrl(
              adLoadedCallbackUrl,
              callback,
              unit_level_params,
              undefined,
              undefined,
              { [event]: "true", container: containerName },
            );
            sendPingback(url);
            if (__DEBUG__) {
              console.log(
                `${CONSOLE_CAPTURE_PREFIX} ${event} for container: '${containerName}'`,
              );
            }
          };

          onDomReady(() => {
            const container = document.getElementById(containerName);
            if (!container) return;

            // Ad-load observers — bounded by the ad-load timeout. Skip if the
            // ad-load window already closed (timeout / page exit fired first).
            if (!disposed) {
              containerObservers.push(observeAdContainer(container, fireEvent));
            }
            // Viewport signal — page lifetime; self-disconnects on first view.
            observeInView(container, () => fireEvent(TrackingEvent.InView));
          });
        }
      }
    }
  }

  function disconnectObservers() {
    disposed = true;
    for (const observers of containerObservers) observers.disconnect();
  }

  // Timeout logic
  setTimeout(() => {
    if (page_options?.rsoc?.callback) {
      for (const [
        containerName,
        adLoadedCallbackUrl,
      ] of pendingUnits.entries()) {
        sendNeverRanPingback(
          adLoadedCallbackUrl,
          page_options.rsoc.callback,
          { container: containerName } as any,
          `timeout_${(timeoutMs / 1000).toString()}s`,
        );
        pendingUnits.delete(containerName);
      }
    }
    disconnectObservers();
  }, timeoutMs);

  // Page unload/visibilitychange logic
  const firePendingPingbacks = () => {
    if (page_options?.rsoc?.callback) {
      for (const [
        containerName,
        adLoadedCallbackUrl,
      ] of pendingUnits.entries()) {
        sendNeverRanPingback(
          adLoadedCallbackUrl,
          page_options.rsoc.callback,
          { container: containerName } as any,
          "page_exit",
        );
        pendingUnits.delete(containerName);
      }
    }
    disconnectObservers();
  };
  window.addEventListener("beforeunload", firePendingPingbacks);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      firePendingPingbacks();
    }
  });

  if (__DEBUG__) {
    (window as any)._rampjsDebugPingback = (
      reason: string,
      containerName?: string,
    ) => {
      if (!page_options?.rsoc?.callback) {
        console.warn("[ASCENT] No RSoC callback config available");
        return;
      }
      const cb = page_options.rsoc.callback;
      if (containerName) {
        const adLoadedCallbackUrl = pendingUnits.get(containerName);
        if (!adLoadedCallbackUrl) {
          console.warn(
            `[ASCENT] Container '${containerName}' not found in pendingUnits. Available:`,
            [...pendingUnits.keys()],
          );
          return;
        }
        sendNeverRanPingback(
          adLoadedCallbackUrl,
          cb,
          { container: containerName } as any,
          reason,
        );
        console.log(
          `[ASCENT] Debug pingback sent: reason='${reason}', container='${containerName}'`,
        );
      } else {
        if (pendingUnits.size === 0) {
          console.warn(
            "[ASCENT] No pending units. CSA may have already fired. Use ?rampjs_delay_csa=true to hold the _googCsa call.",
          );
          return;
        }
        for (const [name, url] of pendingUnits.entries()) {
          sendNeverRanPingback(url, cb, { container: name } as any, reason);
          console.log(
            `[ASCENT] Debug pingback sent: reason='${reason}', container='${name}'`,
          );
        }
      }
    };
    console.log(
      `${CONSOLE_CAPTURE_PREFIX} Debug helper available: _rampjsDebugPingback(reason, containerName?)`,
      `\n  Examples:`,
      `\n    _rampjsDebugPingback("missing_divs")`,
      `\n    _rampjsDebugPingback("timeout_10s")`,
      `\n    _rampjsDebugPingback("csa_script_failed_to_load")`,
      `\n    _rampjsDebugPingback("page_exit")`,
      `\n    _rampjsDebugPingback("mutation_observed", "container_id")`,
      `\n    _rampjsDebugPingback("resize_observed", "container_id")`,
      `\n  Pending containers:`,
      [...pendingUnits.keys()],
      `\n  Tip: Add ?rampjs_delay_csa=true to the URL to hold the _googCsa call`,
    );
  }
}
